import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import MobilePageHeader from '../components/MobilePageHeader';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Minus, ArrowLeftRight, ArrowDownCircle, ArrowUpCircle, Package } from 'lucide-react';

const StockMovementsPage = () => {
    const { api } = useAuth();
    const [movements, setMovements] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [filterType, setFilterType] = useState('all');
    const [formData, setFormData] = useState({
        product_id: '',
        quantity: '',
        reason: 'Utilisation cuisine'
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [movementsRes, productsRes] = await Promise.all([
                api().get('/stock/movements?limit=200'),
                api().get('/products')
            ]);
            setMovements(movementsRes.data);
            setProducts(productsRes.data);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await api().post('/stock/exit', {
                ...formData,
                quantity: parseFloat(formData.quantity)
            });
            toast.success('Sortie de stock enregistrée');
            setDialogOpen(false);
            setFormData({ product_id: '', quantity: '', reason: 'Utilisation cuisine' });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const selectedProduct = products.find(p => p.id === formData.product_id);

    const filteredMovements = movements.filter(m => {
        if (filterType === 'all') return true;
        return m.movement_type === filterType;
    });

    if (loading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-8 bg-muted rounded w-48"></div>
                <div className="h-96 bg-muted rounded-xl"></div>
            </div>
        );
    }

    return (
        <div className="space-y-4 md:space-y-6" data-testid="stock-movements-page">
            {/* Mobile Header with back button */}
            <MobilePageHeader 
                title="Mouvements Stock" 
                subtitle="Entrées et sorties"
                icon={Package}
            />
            
            {/* Desktop Header - hidden on mobile */}
            <div className="hidden md:flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold text-foreground tracking-tight">
                        Mouvements de Stock
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Historique des entrées et sorties
                    </p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-terracotta hover:bg-terracotta/90 btn-press" data-testid="add-exit-btn">
                            <Minus className="w-4 h-4 mr-2" />
                            Sortie de stock
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="font-display">Enregistrer une sortie</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div className="space-y-2">
                                <Label htmlFor="product">Produit</Label>
                                <Select 
                                    value={formData.product_id} 
                                    onValueChange={(value) => setFormData({...formData, product_id: value})}
                                    required
                                >
                                    <SelectTrigger data-testid="exit-product-select">
                                        <SelectValue placeholder="Sélectionner un produit..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {products.map((product) => (
                                            <SelectItem key={product.id} value={product.id}>
                                                {product.name} - Stock: {product.stock_quantity} {product.unit}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {selectedProduct && (
                                <div className="p-3 bg-muted/50 rounded-lg text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Stock disponible:</span>
                                        <span className="font-mono font-semibold">
                                            {selectedProduct.stock_quantity} {selectedProduct.unit}
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="quantity">Quantité à sortir</Label>
                                <Input
                                    id="quantity"
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    max={selectedProduct?.stock_quantity || undefined}
                                    value={formData.quantity}
                                    onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                                    placeholder="0"
                                    required
                                    data-testid="exit-quantity-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="reason">Raison</Label>
                                <Select 
                                    value={formData.reason} 
                                    onValueChange={(value) => setFormData({...formData, reason: value})}
                                >
                                    <SelectTrigger data-testid="exit-reason-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Utilisation cuisine">Utilisation cuisine</SelectItem>
                                        <SelectItem value="Perte / Périmé">Perte / Périmé</SelectItem>
                                        <SelectItem value="Casse">Casse</SelectItem>
                                        <SelectItem value="Don">Don</SelectItem>
                                        <SelectItem value="Inventaire correction">Inventaire correction</SelectItem>
                                        <SelectItem value="Autre">Autre</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                    Annuler
                                </Button>
                                <Button type="submit" className="bg-terracotta hover:bg-terracotta/90" data-testid="exit-submit-btn">
                                    Enregistrer la sortie
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Filter */}
            <div className="flex gap-2">
                <Button
                    variant={filterType === 'all' ? 'default' : 'outline'}
                    onClick={() => setFilterType('all')}
                    size="sm"
                    data-testid="filter-all-btn"
                >
                    <ArrowLeftRight className="w-4 h-4 mr-2" />
                    Tous
                </Button>
                <Button
                    variant={filterType === 'entry' ? 'default' : 'outline'}
                    onClick={() => setFilterType('entry')}
                    size="sm"
                    className={filterType === 'entry' ? 'bg-green-600 hover:bg-green-700' : ''}
                    data-testid="filter-entry-btn"
                >
                    <ArrowDownCircle className="w-4 h-4 mr-2" />
                    Entrées
                </Button>
                <Button
                    variant={filterType === 'exit' ? 'default' : 'outline'}
                    onClick={() => setFilterType('exit')}
                    size="sm"
                    className={filterType === 'exit' ? 'bg-red-600 hover:bg-red-700' : ''}
                    data-testid="filter-exit-btn"
                >
                    <ArrowUpCircle className="w-4 h-4 mr-2" />
                    Sorties
                </Button>
            </div>

            {/* Movements Table */}
            <Card>
                <div className="rounded-xl border border-border overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-muted/30">
                                <TableHead className="font-medium">Date</TableHead>
                                <TableHead className="font-medium">Type</TableHead>
                                <TableHead className="font-medium">Produit</TableHead>
                                <TableHead className="font-medium text-right">Quantité</TableHead>
                                <TableHead className="hidden sm:table-cell font-medium text-right">Avant</TableHead>
                                <TableHead className="hidden sm:table-cell font-medium text-right">Après</TableHead>
                                <TableHead className="hidden md:table-cell font-medium">Raison</TableHead>
                                <TableHead className="hidden lg:table-cell font-medium">Par</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredMovements.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center py-12">
                                        <ArrowLeftRight className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                                        <p className="text-muted-foreground">Aucun mouvement</p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredMovements.map((movement) => (
                                    <TableRow key={movement.id} className="hover:bg-muted/20" data-testid={`movement-row-${movement.id}`}>
                                        <TableCell className="font-mono text-sm">
                                            {new Date(movement.created_at).toLocaleDateString('fr-FR')}
                                            <span className="block text-xs text-muted-foreground">
                                                {new Date(movement.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <Badge 
                                                variant="outline"
                                                className={movement.movement_type === 'entry' 
                                                    ? 'bg-green-50 text-green-700 border-green-200' 
                                                    : 'bg-red-50 text-red-700 border-red-200'
                                                }
                                            >
                                                {movement.movement_type === 'entry' ? 'Entrée' : 'Sortie'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="font-medium">{movement.product_name}</TableCell>
                                        <TableCell className={`text-right font-mono font-semibold ${
                                            movement.movement_type === 'entry' ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                            {movement.movement_type === 'entry' ? '+' : '-'}{movement.quantity}
                                        </TableCell>
                                        <TableCell className="hidden sm:table-cell text-right font-mono text-muted-foreground">
                                            {movement.previous_stock}
                                        </TableCell>
                                        <TableCell className="hidden sm:table-cell text-right font-mono font-semibold">
                                            {movement.new_stock}
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell text-muted-foreground text-sm">
                                            {movement.reason}
                                        </TableCell>
                                        <TableCell className="hidden lg:table-cell text-muted-foreground text-sm">
                                            {movement.created_by_name}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </Card>
        </div>
    );
};

export default StockMovementsPage;
